import { DateTime } from "luxon";
import { Response } from "@models/Models";
import { getCurrentCulture } from "@utils";
import {
	ResultsMenuSport,
	ResultsFormattedData,
	MappedEvent,
} from "@data-types/results";
import { EventsRequestFilter } from "@api-types/events";
import BettingTypesApiService from "@api-services/platform/BettingTypesApiService";
import EventsApiService from "@api-services/platform/EventsApiService";
import EventsPerTournamentApiService from "@api-services/platform/EventsPerTournamentApiService";
import {
	BETTING_TYPES_LIST,
	createMenuFromTournamentEvents,
	findItemIndex,
	getOrderedEvents,
	makeCategory,
	makeDesktopTournament,
	makeFinalItemModel,
	makeMappedEvent,
	makeSport,
} from "./helpers";

export default class ResultsService {
	static async getMenu(
		startDayFromToday: number
	): Promise<Response<ResultsMenuSport[]>> {
		const events = await EventsPerTournamentApiService.getEvents({
			startDayFromToday,
			endDayFromToday: null,
		});

		return new Response(createMenuFromTournamentEvents(events));
	}

	static async getBettingTypeIds() {
		const lookup = await BettingTypesApiService.findLookup();
		return lookup.item
			.filter((bt) => BETTING_TYPES_LIST.includes(bt.abrv))
			.map((bt) => bt.id);
	}

	static async getResults(
		filter: EventsRequestFilter,
		bettingTypeIds: string[]
	): Promise<Response<ResultsFormattedData<Map<string, MappedEvent[]>>[]>> {
		const fetchedEvents = await EventsApiService.getEventResults(
			filter,
			bettingTypeIds
		);

		const formattedResult = getOrderedEvents(
			fetchedEvents,
			filter.tournamentIds
		).reduce<ResultsFormattedData<Map<string, MappedEvent[]>>[]>(
			(acc, item) => {
				const eventData = item.event;
				const eventDate = DateTime.fromISO(
					eventData.startTime
				).toFormat("cccc, dd.LL", { locale: getCurrentCulture() });
				const itemWithSportAndCategoryIdx = findItemIndex(acc, item);
				const mappedEvent = makeMappedEvent(item);

				if (itemWithSportAndCategoryIdx > -1) {
					const existingItem = acc[itemWithSportAndCategoryIdx];

					const existingTournamentIdx =
						existingItem.tournaments.findIndex(
							(t) => t.id === eventData.tournament.id
						);

					if (existingTournamentIdx > -1) {
						if (
							existingItem.tournaments[
								existingTournamentIdx
							].events.has(eventDate)
						) {
							existingItem.tournaments[
								existingTournamentIdx
							].events
								.get(eventDate)
								?.push(mappedEvent);
						} else {
							existingItem.tournaments[
								existingTournamentIdx
							].events.set(eventDate, [mappedEvent]);
						}
					} else {
						existingItem.tournaments.push(
							makeDesktopTournament(
								eventData,
								eventDate,
								mappedEvent
							)
						);
					}
				} else {
					const sport = makeSport(eventData);
					const sportCategory = makeCategory(eventData);
					const tournament = makeDesktopTournament(
						eventData,
						eventDate,
						mappedEvent
					);
					const itemFinalModel = makeFinalItemModel(
						sport,
						sportCategory,
						tournament
					);

					acc.push(itemFinalModel);
				}

				return acc;
			},
			[]
		);

		return new Response(formattedResult);
	}
}
